import { Link, createFileRoute } from "@tanstack/react-router";
import { SocialIcon } from "react-social-icons";
import NavBar from "#/components/navbar/navbar";
import Footer from "#/components/footer/footer";
import Mdx from "#/components/MdxRemote";
import Author from "#/components/author";
import { organization } from "#/lib/jsonLdSchema";
import { metadataToHead } from "#/lib/seo";

export default function Page() {
  return (
    <>
      <header>
        <NavBar />
      </header>
      <main id="content" className="mx-auto mt-24 max-w-3xl space-y-10">
        <h1 className="text-center text-5xl">Contact Roamdoc</h1>

        <section className="mx-8 space-y-5">
          <Mdx source={content} />

          <ContactForm />
        </section>

        <section className="mx-8 space-y-5">
          <Mdx source={content2} />
        </section>

        <section className="mx-8 mb-10 space-y-5">
          <h2 className="mb-5 mt-16 text-3xl font-bold text-sky-600">
            Roamdoc Social Media
          </h2>

          <div className=" flex space-x-3">
            <SocialIcon
              url="https://linkedin.com/company/roamdoc"
              target="_blank"
            />
            <SocialIcon url="https://twitter.com/roamdoc" target="_blank" />
            <SocialIcon url="https://youtube.com/@roamdoc" target="_blank" />
            <SocialIcon url="https://instagram.com/roamdoc" target="_blank" />
            <SocialIcon
              url="https://www.facebook.com/roamdoc"
              target="_blank"
            />
          </div>

          <h2 className="mb-10 pt-10 text-3xl font-bold text-sky-600">
            About Roamdoc
          </h2>

          <p>
            Roamdoc is a job search engine for physicians in anesthesiology.
          </p>

          <Link
            to="/about-roamdoc"
            className="rounded border border-blue-500 bg-transparent px-4 py-2 font-semibold text-blue-700 no-underline hover:border-transparent hover:bg-blue-500 hover:text-white "
          >
            Learn more about Roamdoc
          </Link>
        </section>

        <Author />
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={addJsonLd()}
      />
    </>
  );
}

const content = `
Connect with us, we specialize in connecting top talent with premier opportunities in the medical field. Our focus is on Anesthesiologists and Certified Registered Nurse Anesthetists (CRNAs), offering a broad spectrum of roles including permanent positions and locum tenens.

## Get in Touch with Our Support Team
Fill out the form below.
`;

const content2 = `
## Feedback and Suggestions
We are always looking for ways to improve our service. If you have any feedback or suggestions, please let us know.

## Frequently Asked Questions (FAQs)
### How do I post a job?
To post a job, please contact us by filling out the form above.

### How do I remove a job posting?
To remove a job posting, please contact us by filling out the form above.

### How do I edit a job posting?
To edit a job posting, please contact us by filling out the form above.

### How do I contact a recruiter?
To contact a recruiter, please contact us by filling out the form above.

### How do I apply for a job posting?
To apply for a job posting, click the "Apply Now" button on the job posting page.

### How do I create a job alert?
To create a job alert, click the "Create Job Alert" button on the job posting page.

## Media and Press Inquiries
For media and press inquiries, please email us by filling out the form above. 
`;

const ContactForm: React.FC = () => {
  return (
    <form className="mx-auto my-10 max-w-lg rounded-lg bg-white p-6 shadow-md">
      <div className="mb-4">
        <label
          htmlFor="name"
          className="mb-2 block text-sm font-bold text-gray-700"
        >
          Name
        </label>
        <input
          type="text"
          id="name"
          name="name"
          className="focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none"
        />
      </div>
      <div className="mb-4">
        <label
          htmlFor="email"
          className="mb-2 block text-sm font-bold text-gray-700"
        >
          Email
        </label>
        <input
          type="email"
          id="email"
          name="email"
          className="focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none"
        />
      </div>
      <div className="mb-6">
        <label
          htmlFor="comment"
          className="mb-2 block text-sm font-bold text-gray-700"
        >
          Comment
        </label>
        <textarea
          id="comment"
          name="comment"
          className="focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none"
          rows={4}
        ></textarea>
      </div>
      <div className="flex items-center justify-between">
        <button
          type="submit"
          className="focus:shadow-outline rounded bg-blue-500 px-4 py-2 font-bold text-white hover:bg-blue-700 focus:outline-none"
        >
          Send Message
        </button>
      </div>
    </form>
  );
};

export const metadata = {
  title: "Contact Roamdoc • Job search engine & recruiting for physicians",
  description:
    "Contact Roamdoc, we specialize in connecting top talent with premier opportunities in the medical field.",
  icons: [{ rel: "icon", url: "/favicon.ico" }],
  openGraph: {
    images: ["https://www.roamdoc.com/og-image.png"],
  },
  alternates: {
    canonical: `https://www.roamdoc.com/contact-roamdoc`,
  },
};

function addJsonLd() {
  return {
    __html: `{
      "@context": "https://schema.org/",
      "@type": "ContactPage",
      "url": "https://www.roamdoc.com/contact-roamdoc",
      "inLanguage": "en-US",
      "mainEntityOfPage": ${JSON.stringify(organization())},
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Customer Support",
        "url": "https://www.roamdoc.com/contact-roamdoc",
        "areaServed": "US",
        "availableLanguage": ["English", "Spanish"]
      }
    }`,
  };
}

export const Route = createFileRoute("/contact-roamdoc/")({
  component: Page,
  head: () => metadataToHead(metadata),
});
